import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CalendarFold,
  NotebookTabs,
  Inbox,
  Users,
  LogOutIcon,
  ShieldUser,
  ChevronDown,
  KeyRound,
  CalendarPlus
} from "lucide-react";

const Sidebar = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [user, setUser] = useState({
    username: 'Loading...',
    email: 'Loading...',
  });

  // Auth0 Configuration
  const config = {
    domain: "vms-optimus.us.auth0.com",
    clientId: "fcnJvdlwufooitjKCG0OFiw5eXF317Fn",
    clientSecret: "q1ze1Rpxg19qf0jXn6_IYrm0LDQqWtmVA6fW1ZMG0Bhw1XiNgZmZlXO0rdk4djtm",
    managementClientId: "ZxowKlLpWMVvNbUuzMKooWx0FA5jhxYg",
    managementClientSecret: "XTH0sFAFWTqpZe8gYn9PJtVU4rg-6hctN7PROhojbsQJ2ZSVy7Nabk5kZSoihlnm",
    audience: "https://vms-optimus.us.auth0.com/api/v2/"
  };

  // Get user role from localStorage
  const userData = JSON.parse(localStorage.getItem("user"));
  const userRole = userData?.role || 'Employee';
  const isAdmin = userRole === "Admin";

  const allNavigationItems = [
    { icon: CalendarFold, path: "/invite", name: "Invitation" },
    { icon: NotebookTabs, path: "/adbook", name: "Address Book" },
    { icon: Inbox, path: "/log", name: "Logbook" },
    // { icon: CalendarPlus, path: "/events", name: "Events" },   
    { icon: Users, path: "/user", name: "User Access", adminOnly: true },
    { icon: ShieldUser, path: "/employees", name: "Manage Employees", adminOnly: true },
  ];

  // Filter navigation items based on user role
  const navigationItems = allNavigationItems.filter(
    (item) => !item.adminOnly || isAdmin
  );

  // Fetch Management API access token
  const getManagementAccessToken = async () => {
    try {
      const response = await fetch(`https://${config.domain}/oauth/token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: config.managementClientId,
          client_secret: config.managementClientSecret,
          audience: config.audience,
          grant_type: "client_credentials",
          scope: "read:users read:users_app_metadata update:users_app_metadata read:user_idp_tokens read:roles read:role_members",
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get management token');
      }

      const data = await response.json();
      return data.access_token;
    } catch (error) {
      console.error("Error getting management token:", error);
      return null;
    }
  };

  // Fetch current user data from Auth0
  useEffect(() => {
    let isMounted = true;

    const fetchCurrentUser = async () => {
      try {
        // Get stored user info
        const storedUser = JSON.parse(localStorage.getItem("user"));

        if (!storedUser || !storedUser.email) {
          console.error("No user found in storage");
          if (isMounted) {
            navigate('/login');
          }
          return;
        }

        // Get management API token
        const token = await getManagementAccessToken();

        if (!token) {
          console.error("Failed to get management token");
          if (isMounted) {
            setUser(storedUser); // Use stored data as fallback
          }
          return;
        }

        // Fetch all users and find current user
        const response = await fetch(`${config.audience}users`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        });

        if (!isMounted) return;

        if (response.ok) {
          const allUsers = await response.json();
          const auth0User = allUsers.find((u) => u.email === storedUser.email);

          if (auth0User) {
            const userData = {
              username: auth0User.user_metadata?.username ||
                auth0User.username ||
                auth0User.email.split("@")[0],
              email: auth0User.email
            };

            if (isMounted) {
              setUser(userData);
              localStorage.setItem("user", JSON.stringify({ ...storedUser, ...userData }));
              console.log("Fetched User from Auth0:", userData);
            }
          } else {
            if (isMounted) {
              setUser(storedUser);
            }
          }
        } else {
          console.error("Failed to fetch users:", await response.text());
          if (isMounted) {
            setUser(storedUser);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        const storedUser = JSON.parse(localStorage.getItem("user"));
        if (storedUser && isMounted) {
          setUser(storedUser);
        }
      }
    };

    fetchCurrentUser();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // Handle password reset
  const handleResetPassword = async () => {
    try {
      const response = await fetch(
        `https://${config.domain}/dbconnections/change_password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            client_id: config.clientId,
            email: user.email,
            connection: "Username-Password-Authentication",
          }),
        }
      );

      if (response.ok) {
        setIsOverlayOpen(true);
        setDropdownOpen(false);
        setTimeout(() => setIsOverlayOpen(false), 3000);
      } else {
        const errorData = await response.json();
        console.error("Password reset error:", errorData);
      }
    } catch (error) {
      console.error("Error sending password reset email:", error);
    }
  };

  // Handle logout
  const handleLogout = () => {
    setDropdownOpen(false);
    if (onLogout) {
      onLogout();
    }
  };

  return (
    <>
      <div
        className={`bg-white flex flex-col items-start py-3 z-50 my-auto rounded-lg fixed left-4 inset-y-4 shadow-lg transition-all duration-300 ${isExpanded ? "w-[17rem]" : "w-20"
          }`}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => {
          setIsExpanded(false);
          setDropdownOpen(false);
        }}
      >
        {/* Logo */}
        <div className="mb-8 mt-2 px-6 relative h-9 flex items-center">
          <img
            src="/Vector.svg"
            alt="Logo"
            className={`absolute w-8 h-8 transition-opacity duration-300 ${isExpanded ? "opacity-0" : "opacity-100"
              }`}
          />
          <img
            src="/Vector.svg"
            alt="Logo Full"
            className={`w-8 h-8 transition-opacity duration-300 ${isExpanded ? "opacity-100" : "opacity-0"
              }`}
          />
        </div>

        {/* Navigation Icons */}
        <div className="flex flex-col space-y-4 w-full flex-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;

            return (
              <div
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex items-center py-4 cursor-pointer transition-colors ${isExpanded ? "px-6" : "justify-center"
                  } ${isActive ? "bg-gray-100 rounded-lg mx-2" : "hover:bg-gray-100 hover:rounded-lg mx-2"
                  }`}
              >
                <Icon
                  className={`w-5 h-5 flex-shrink-0 ${isActive ? "text-gray-800" : "text-gray-600"
                    }`}
                />
                {isExpanded && (
                  <span
                    className={`ml-4 whitespace-nowrap font-medium ${isActive ? "text-gray-800" : "text-gray-600"
                      }`}
                  >
                    {item.name}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* User Profile Section */}
        <div className="w-full px-2 pb-2 border-t border-gray-200 pt-3">
          <div
            className={`flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors relative ${isExpanded ? "px-3" : "justify-center"
              }`}
            onClick={() => isExpanded && setDropdownOpen(!dropdownOpen)}
          >
            {/* Avatar */}
            <div className="w-9 h-9 rounded-full bg-blue-800 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
              {user.username ? user.username.charAt(0).toUpperCase() : 'U'}
            </div>

            {isExpanded && (
              <>
                {/* User Info */}
                <div className="flex-1 min-w-0 ml-3">
                  <p className="text-sm text-gray-900 font-medium truncate">
                    {user.username}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user.email}
                  </p>
                </div>

                {/* Dropdown Arrow */}
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${dropdownOpen ? 'transform rotate-180' : ''
                    }`}
                />
              </>
            )}
          </div>

          {/* Dropdown Menu */}
          {dropdownOpen && isExpanded && (
            <div className="mt-2 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
              <button
                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  handleResetPassword();
                }}
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Reset Password
              </button>
              <button
                className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100 flex items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLogout();
                }}
              >
                <LogOutIcon className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Password Reset Overlay */}
      {isOverlayOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg text-center max-w-sm mx-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Password Reset Sent
            </h2>
            <p className="text-gray-600 text-sm">
              A reset link has been sent to <strong>{user.email}</strong>. Please check your inbox.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;